import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Event } from "@uno/protocol";
import { Howler } from "howler";
import { AppContext } from "context";
import { useSounds } from "context/SoundContext";
import useLocalStorage from "hook/useLocalStorage";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import getPublicAssetUrl from "utils/PublicAsset";
type View = "menu" | "language" | "sound";

const LANGUAGES = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "pt", label: "Português", flag: "br" },
];

const SettingsDropdown = () => {
    const { socket } = React.useContext(AppContext);
    const { i18n, t } = useTranslation();
    const { play } = useSounds();
    const [storedVolume, setStoredVolume] = useLocalStorage("LASTDOTCARDS.VOLUME");
    const [hapticEnabled, setHapticEnabled] = useLocalStorage<boolean>("LASTDOTCARDS.HAPTIC", true);
    const [isOpen, setIsOpen] = React.useState(false);
    const [view, setView] = React.useState<View>("menu");
    const [language, setLanguage] = React.useState(i18n.language);
    const [volume, setVolume] = React.useState(storedVolume ? parseFloat(storedVolume) : 0.2);
    const [direction, setDirection] = React.useState(1);

    React.useEffect(() => {
        Howler.volume(volume);
    }, [volume]);

    const handleClose = () => {
        setIsOpen(false);

        setTimeout(() => setView("menu"), 200);
    };

    const goToView = (target: View) => {
        setDirection(target === "menu" ? -1 : 1);
        setView(target);
    };

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setLanguage(lang);
        socket?.emit(Event.PlayerChangeLanguage, lang);
        handleClose();
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVolume(parseFloat(e.target.value));
    };

    const handleVolumeCommit = () => {
        setStoredVolume(volume.toString());
        play("click");
    };


    const currentFlag = LANGUAGES.find(l => l.code === language)?.flag || language;

    const volumeIcon = volume === 0 ? (
        <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
        </svg>
    ) : volume < 0.5 ? (
        <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072" />
        </svg>
    ) : (
        <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
    );

    return (
        <div className="relative">
            <ButtonHapticWrapper>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    data-settings-button
                    className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-colors cursor-pointer"
                >
                    <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </button>
            </ButtonHapticWrapper>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={handleClose} />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -4 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -4 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                            className="absolute top-full left-0 mt-2 z-50 bg-black/40 backdrop-blur-xl border border-white/15 rounded-xl shadow-2xl overflow-hidden"
                            style={{ minWidth: 180 }}
                        >
                            <AnimatePresence mode="wait" initial={false}>
                                {view === "menu" && (
                                    <motion.div
                                        key="menu"
                                        initial={{ x: direction < 0 ? -40 : 0, opacity: direction < 0 ? 0 : 1 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -40, opacity: 0 }}
                                        transition={{ duration: 0.15, ease: "easeInOut" }}
                                    >
                                        <button
                                            onClick={() => goToView("language")}
                                            className="w-full flex items-center justify-between px-3.5 py-3 hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2.5">
                                                <img src={getPublicAssetUrl(`assets/flags/${currentFlag}.svg`)} alt={language} className="w-4 h-4" />
                                                <span className="text-white/80 text-sm font-changa-one">{t("settings.language")}</span>
                                            </span>
                                            <svg className="w-3.5 h-3.5 text-white/30" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => goToView("sound")}
                                            className="w-full flex items-center justify-between px-3.5 py-3 hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2.5">
                                                {volumeIcon}
                                                <span className="text-white/80 text-sm font-changa-one">{t("settings.sound")}</span>
                                            </span>
                                            <svg className="w-3.5 h-3.5 text-white/30" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => setHapticEnabled(!hapticEnabled)}
                                            className="w-full flex items-center justify-between px-3.5 py-3 hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2.5">
                                                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-white/80 text-sm font-changa-one">{t("settings.vibrations")}</span>
                                            </span>
                                            <div className={`w-9 h-5 rounded-full transition-colors ${hapticEnabled ? "bg-blue-500" : "bg-white/15"}`}>
                                                <div className={`w-4 h-4 mt-0.5 rounded-full bg-white shadow-sm transition-transform ${hapticEnabled ? "ml-[18px]" : "ml-0.5"}`} />
                                            </div>
                                        </button>
                                    </motion.div>
                                )}

                                {view === "language" && (
                                    <motion.div
                                        key="language"
                                        initial={{ x: 40, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: 40, opacity: 0 }}
                                        transition={{ duration: 0.15, ease: "easeInOut" }}
                                    >
                                        <button
                                            onClick={() => goToView("menu")}
                                            className="w-full flex items-center gap-2 px-3.5 py-2.5 border-b border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                            </svg>
                                            <span className="text-white/50 text-xs font-changa-one">{t("settings.language")}</span>
                                        </button>
                                        {LANGUAGES.map(({ code, label }) => (
                                            <button
                                                key={code}
                                                onClick={() => changeLanguage(code)}
                                                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-white/10 transition-colors cursor-pointer ${code === language ? "bg-white/10" : ""}`}
                                            >
                                                <img src={getPublicAssetUrl(`assets/flags/${LANGUAGES.find(l => l.code === code)?.flag || code}.svg`)} alt={code} className="w-4 h-4" />
                                                <span className="text-white/80 text-sm font-changa-one">{label}</span>
                                            </button>
                                        ))}
                                    </motion.div>
                                )}

                                {view === "sound" && (
                                    <motion.div
                                        key="sound"
                                        initial={{ x: 40, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: 40, opacity: 0 }}
                                        transition={{ duration: 0.15, ease: "easeInOut" }}
                                    >
                                        <button
                                            onClick={() => goToView("menu")}
                                            className="w-full flex items-center gap-2 px-3.5 py-2.5 border-b border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                            </svg>
                                            <span className="text-white/50 text-xs font-changa-one">{t("settings.sound")}</span>
                                        </button>
                                        <div className="px-3.5 py-4 flex items-center gap-3">
                                            {volumeIcon}
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={volume}
                                                onChange={handleVolumeChange}
                                                onMouseUp={handleVolumeCommit}
                                                onTouchEnd={handleVolumeCommit}
                                                className="flex-1 h-1.5 appearance-none rounded-full outline-none cursor-pointer touch-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md"
                                                style={{
                                                    background: `linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) ${volume * 100}%, rgba(255,255,255,0.15) ${volume * 100}%)`,
                                                }}
                                            />
                                        </div>
                                    </motion.div>
                                )}

                            </AnimatePresence>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SettingsDropdown;
